import { ArrowRight, Sofa, Armchair, Bed, Hotel } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';

const categories = [
  {
    id: 1,
    name: 'Garniture',
    slug: 'garniture',
    description: 'Elegantne i udobne garniture za dnevni boravak',
    icon: Sofa,
  },
  {
    id: 2,
    name: 'Stolice',
    slug: 'stolice',
    description: 'Trpezarijske i kancelarijske stolice vrhunskog kvaliteta',
    icon: Armchair,
  },
  {
    id: 3,
    name: 'Lože',
    slug: 'loze',
    description: 'Kompletne spavaće sobe za potpuni komfor',
    icon: Hotel,
  },
  {
    id: 4,
    name: 'Kreveti',
    slug: 'kreveti',
    description: 'Kvalitetni kreveti za savršen san',
    icon: Bed,
  },
];

export default function FeaturedProducts() {
  const navigate = useNavigate();

  return (
    <section id="products" className="py-20 lg:py-28 relative overflow-hidden">
      {/* POZADINA SA NAMEŠTAJEM */}
      <div className="absolute inset-0 z-0">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/40 via-background to-secondary/30" />
        
        {/* Blur-ovane slike nameštaja u pozadini */}
        <div className="absolute top-10 right-0 w-[500px] h-[500px] opacity-15">
          <img 
            src="https://images.unsplash.com/photo-1604578762246-41134e37f9cc?q=80&w=1000" 
            alt=""
            className="w-full h-full object-cover rounded-3xl"
            style={{ filter: 'blur(15px)' }}
          />
        </div>
        
        <div className="absolute bottom-0 left-0 w-[450px] h-[450px] opacity-12">
          <img 
            src="https://images.unsplash.com/photo-1631679706909-1844bbd07221?q=80&w=1000" 
            alt=""
            className="w-full h-full object-cover rounded-3xl"
            style={{ filter: 'blur(18px)' }}
          />
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground mb-4 lg:mb-6">
            Naša kolekcija
          </h2>
          <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
            Istražite našu široku paletu nameštaja za svaku prostoriju u vašem domu
          </p>
        </div>

        {/* Kategorije: uvek 2 × 2 (dve kartice po redu, dva reda) */}
        <div className="mx-auto max-w-4xl grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {categories.map((category, index) => {
            const Icon = category.icon;
            const go = () => navigate(`/proizvodi/${category.slug}`);
            return (
              <Card
                key={category.id}
                role="button"
                tabIndex={0}
                onClick={go}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    go();
                  }
                }}
                className="group relative overflow-hidden border-2 border-border/50 hover:border-primary/30 transition-all duration-500 hover-shadow card-shadow bg-card/90 backdrop-blur-sm cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                style={{
                  animationDelay: `${index * 100}ms`,
                  animation: 'fadeInUp 0.6s ease-out forwards',
                  opacity: 0,
                  touchAction: 'manipulation',
                }}
              >
                <CardContent className="p-8 lg:p-10 relative z-0">
                  <div className="mb-6 lg:mb-8">
                    <div className="w-16 h-16 lg:w-20 lg:h-20 bg-primary/5 rounded-2xl flex items-center justify-center group-hover:bg-primary/10 transition-colors duration-300 border-2 border-primary/10 group-hover:border-primary/20">
                      <Icon className="w-8 h-8 lg:w-10 lg:h-10 text-primary" strokeWidth={1.5} />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                        {category.name}
                      </h3>
                      <p className="text-sm lg:text-base text-muted-foreground leading-relaxed">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="mt-16 lg:mt-20 text-center">
          <Button
            size="lg"
            variant="default"
            className="bg-[#5c4033] hover:bg-[#4a3428] active:bg-[#3d2b20] text-[#faf6f2] shadow-md hover:shadow-lg font-medium px-10 py-6 touch-manipulation cursor-pointer relative z-10 border-0 focus-visible:ring-2 focus-visible:ring-[#5c4033]/45 focus-visible:ring-offset-2 [&_svg]:text-[#faf6f2]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate('/proizvodi/sve');
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate('/proizvodi/sve');
            }}
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
          >
            Pogledajte celokupnu ponudu
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
